"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "AI Threat Detection",
    description: "Advanced machine learning algorithms that identify and neutralize cyber threats in real-time before they impact your operations.",
    Icon: LineChart
  }, {
    number: "02",
    title: "24/7 Security Monitoring",
    description: "Continuous surveillance of your digital infrastructure, ensuring immediate response to any suspicious activity around the clock.",
    Icon: Brain
  }, {
    number: "03",
    title: "Phishing Prevention",
    description: "Proactive defense mechanisms designed to filter malicious emails and protect your team from sophisticated social engineering attacks.",
    Icon: Radar
  }, {
    number: "04",
    title: "Vulnerability Assessments",
    description: "Comprehensive audits of your systems to identify weaknesses and provide actionable roadmaps for strengthening your security posture.",
    Icon: Compass
  }, {
    number: "05",
    title: "Security Awareness Training",
    description: "Empowering your employees with the knowledge and skills to recognize and report potential security threats effectively.",
    Icon: Activity
  }, {
    number: "06",
    title: "Incident Response",
    description: "Rapid, expert-led intervention strategies to contain breaches, minimize damage, and restore your business continuity quickly.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="servicedetailfeatures" className="bg-slate-50 text-slate-900 py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs tracking-widest uppercase text-blue-600 mb-6">
              03 / Our Expertise
            </p>
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900">
              AI-Powered Cybersecurity Solutions.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-slate-700 max-w-2xl">
              InfinitySecure combines cutting-edge artificial intelligence with human expertise to provide robust protection for your digital assets. Our comprehensive suite of services ensures your business remains secure, compliant, and resilient against modern cyber threats.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-slate-50 border border-transparent hover:border-blue-600/40 transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-default text-xs tracking-widest text-blue-600">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-blue-600/10 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-8 text-slate-900 group-hover:text-blue-600 transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-default font-semibold text-2xl leading-snug text-slate-900 mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-slate-600 mb-8">
                  {cap.description}
                </p>

                {}
                <div className="mt-auto">
                  <Link to="/ExpertisePage" className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-slate-700 hover:text-blue-600 transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-blue-600 to-blue-600 bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Learn More
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-slate-200">
          <p className="font-default text-sm text-slate-600 max-w-xl">
            Protect your business today with our tailored cybersecurity solutions.
          </p>
          <Button asChild size="lg" className="bg-blue-600 text-white hover:bg-blue-700 font-default text-xs tracking-widest uppercase rounded-lg shadow-md">
            <Link to="/ContactPage">Request a Free Security Assessment</Link>
          </Button>
        </div>
      </div>
    </section>;
}